import { Injectable } from '@angular/core';
import { Observable, of, timer, BehaviorSubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

interface CacheEntry<T> {
  timestamp: number;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private readonly defaultDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  private readonly backgroundUpdateDelay = 100; // 100ms delay for background updates
  private readonly cacheUpdates = new Map<string, BehaviorSubject<any>>();

  generateCacheKey(prefix: string, params: string[]): string {
    const data = [prefix, ...params].join('|');
    return prefix + '_' + btoa(encodeURIComponent(data));
  }

  get<T>(key: string, duration: number = this.defaultDuration): T | null {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    try {
      const entry: CacheEntry<T> = JSON.parse(cached);
      const now = Date.now();

      if (now - entry.timestamp > duration) {
        localStorage.removeItem(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      // Corrupt/partial entry — drop it and behave as a cache miss rather than throwing.
      console.warn(`[CacheService]: Failed to read cache key ${key}`, error);
      localStorage.removeItem(key);
      return null;
    }
  }

  set<T>(key: string, data: T): void {
    const entry: CacheEntry<T> = {
      timestamp: Date.now(),
      data,
    };
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      // Caching is best-effort: a failed write (e.g. QuotaExceededError on a large payload, or a
      // serialization error) must NEVER break the caller. Evict this prefix's entries to free room
      // for next time, then give up silently on this write.
      console.warn(`[CacheService]: Failed to cache key ${key}; skipping`, error);
      this.clearByPrefix(key.split('_')[0]);
      return;
    }

    // Emit the update if there are subscribers
    if (this.cacheUpdates.has(key)) {
      this.cacheUpdates.get(key)?.next(data);
    }
  }

  remove(key: string): void {
    localStorage.removeItem(key);
    this.cacheUpdates.delete(key);
  }

  clearByPrefix(prefix: string): void {
    // Collect first, then remove: removing while iterating by index shifts later entries down,
    // which would skip adjacent matching keys.
    const matching: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        matching.push(key);
      }
    }
    matching.forEach((key) => {
      localStorage.removeItem(key);
      this.cacheUpdates.delete(key);
    });
  }

  /**
   * Write-through update: apply `updater` to the array payload of every cached entry under a
   * prefix, in place. Used after a mutation so a reload reflects the change immediately instead of
   * showing a stale copy (the per-load revalidate still reconciles against the server). Entries
   * whose payload isn't an array, or that fail to parse, are left/dropped without throwing.
   */
  patchByPrefix<T>(prefix: string, updater: (items: T[]) => T[]): void {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      try {
        const entry = JSON.parse(raw) as CacheEntry<T[]>;
        if (Array.isArray(entry.data)) {
          entry.data = updater(entry.data);
          localStorage.setItem(key, JSON.stringify(entry));
        }
      } catch (error) {
        console.warn(`[CacheService]: Failed to patch cache key ${key}; dropping`, error);
        localStorage.removeItem(key);
      }
    });
  }

  private getOrCreateSubject<T>(key: string): BehaviorSubject<T> {
    if (!this.cacheUpdates.has(key)) {
      this.cacheUpdates.set(key, new BehaviorSubject<T>(this.get<T>(key) as T));
    }
    return this.cacheUpdates.get(key) as BehaviorSubject<T>;
  }

  private scheduleBackgroundUpdate<T>(key: string, fetchFn: () => Observable<T>): void {
    timer(this.backgroundUpdateDelay)
      .pipe(
        switchMap(() => fetchFn()),
        tap(result => {
          this.set(key, result);
        })
      )
      .subscribe({
        error: () => {} // Silently handle background update errors
      });
  }

  /**
   * Creates an Observable that either returns cached data or fetches fresh data.
   * In both cases, schedules a background update after a delay.
   * The observable will emit new values when background updates complete.
   * @param key Cache key
   * @param duration Cache duration in milliseconds
   * @param fetchFn Function to fetch fresh data
   * @param useCache Whether to use cache
   * @returns Observable of type T
   */
  getCachedOrFetch<T>(
    key: string,
    duration: number,
    fetchFn: () => Observable<T>,
    useCache: boolean = true
  ): Observable<T> {
    if (!useCache) {
      return fetchFn().pipe(
        tap(result => this.set(key, result))
      );
    }

    const subject = this.getOrCreateSubject<T>(key);
    const cachedResult = this.get<T>(key, duration);

    if (cachedResult) {
      // Update subject with cached data
      subject.next(cachedResult);
      // Schedule background update
      this.scheduleBackgroundUpdate(key, fetchFn);
      return subject.asObservable();
    }

    // No cache, fetch fresh data
    fetchFn().subscribe({
      next: (result) => {
        this.set(key, result);
        // Schedule background update
        this.scheduleBackgroundUpdate(key, fetchFn);
      },
      error: (error) => subject.error(error)
    });

    return subject.asObservable();
  }
}
